/**
 * Board minutes for a company buy-back — a different document, not a variant.
 *
 * Decision 51. A capital increase and a buy-back share a heading and nothing
 * else: nobody subscribes here, no shares are created, and the money goes the
 * other way. So there is **no Tegningsliste**. In its place an *Ervervede
 * aksjer* table — who sold, how many, at what price, for what consideration.
 *
 * ## The three paragraphs that make it a real minute
 *
 * **Authority (§ 9-4).** A board may only buy the company's own shares under an
 * authorisation from the general meeting. The minute names it, and leaves a gap
 * where its date belongs rather than inventing one.
 *
 * **The free-equity limit (§ 9-3).** A buy-back may only be funded from equity
 * the company could otherwise have distributed. **The register cannot compute
 * that** — it holds shares, not accounts — so the document prints the
 * requirement and says it must be confirmed against the last approved annual
 * accounts. The same treatment par value gets, and for the same reason: a figure
 * the data cannot produce is named, never faked.
 *
 * **The consequence (§ 5-4).** Own shares carry no vote. And retiring them is
 * *not* this resolution: that is a capital reduction under chapter 12, with
 * creditor notice, and this minute deliberately stops short of it. A document
 * that quietly retired shares would be worse than one that says where it ends.
 *
 * ## An unsettled buy-back says so
 *
 * Until settlement the shares are still outstanding everywhere in this app, so
 * the minute cannot imply they have gone. It prints a provisional note instead
 * of pretending the transfer completed.
 */
import { useMemo, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { fmtDateAbs } from '@/shared/util/dates'
import { fmtKrExact, fmtKrPrice, fmtKrSum } from '@/shared/util/money'
import type { CompanyDetails } from '@/types/company'
import {
  netShares,
  sharesOutstanding,
  type ShareTransfer,
  type Shareholder,
} from '@/types/ownership'

const nb = (n: number) => n.toLocaleString('nb-NO')

/** Long Norwegian date, as the opening sentence of a minute spells it out. */
function longDate(iso: string): string {
  if (!iso) return '—'
  const months = [
    'januar', 'februar', 'mars', 'april', 'mai', 'juni',
    'juli', 'august', 'september', 'oktober', 'november', 'desember',
  ]
  const [y, m, d] = iso.split('-').map(Number)
  return y && m && d ? `${d}. ${months[m - 1]} ${y}` : iso
}

export function BuybackMinutesModal({
  transfer,
  holders,
  transfers,
  company,
  onClose,
}: {
  transfer: ShareTransfer
  holders: Shareholder[]
  /** The whole ledger — outstanding before and after are read from it. */
  transfers: ShareTransfer[]
  company: CompanyDetails
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  const model = useMemo(() => {
    const seller = holders.find((h) => h.id === transfer.fromId) ?? null
    const consideration =
      transfer.pricePerShare === null ? null : transfer.shares * transfer.pricePerShare
    const outstandingNow = sharesOutstanding(holders, transfers)
    // Settled, the shares are already out of the total; pending, they are still
    // in it. Either way the minute states both sides of the same event.
    const before = transfer.settled ? outstandingNow + transfer.shares : outstandingNow
    const after = before - transfer.shares
    const par = company.parValue
    return {
      seller,
      sellerHeld: seller ? netShares(seller, transfers) : 0,
      consideration,
      before,
      after,
      capitalAfter: par === null ? null : after * par,
      par,
      pct: before > 0 ? (transfer.shares / before) * 100 : null,
    }
  }, [transfer, holders, transfers, company])

  const missing = [
    !company.legalName && 'legal name',
    !company.orgNumber && 'organisation number',
    company.parValue === null && 'par value per share',
    company.board.length === 0 && 'board members',
  ].filter(Boolean) as string[]

  const text = useMemo(() => {
    const c = company
    const m = model
    const name = c.legalName || '[selskapsnavn]'
    const lines = [
      `PROTOKOLL FRA STYREMØTE I ${name.toUpperCase()}`,
      '',
      `Den ${longDate(transfer.date)} ble det avholdt styremøte i ${name}, org.nr. ${c.orgNumber || '[org.nr.]'}.`,
      '',
      'TIL STEDE',
      ...(c.board.length
        ? c.board.map((b) => `  ${b.name}${b.role ? ` — ${b.role}` : ''}`)
        : ['  [styremedlemmer mangler]']),
      '',
      'SAK 1 — GODKJENNING AV INNKALLING OG DAGSORDEN',
      'Innkalling og dagsorden ble enstemmig godkjent.',
      '',
      'SAK 2 — ERVERV AV EGNE AKSJER',
      `Styret behandlet erverv av egne aksjer i medhold av fullmakt fra generalforsamlingen, jf. aksjeloven § 9-4. Fullmakten er gitt [dato for generalforsamlingens fullmakt].`,
      `Selskapet erverver ${nb(transfer.shares)} egne aksjer fra ${m.seller?.name ?? '[selger]'}${
        transfer.pricePerShare === null
          ? ''
          : ` til kurs ${fmtKrPrice(transfer.pricePerShare)} per aksje`
      }.`,
      m.consideration === null
        ? 'Samlet vederlag er ikke fastsatt i registeret.'
        : `Samlet vederlag utgjør ${fmtKrExact(m.consideration)}.`,
      `Vederlaget ligger innenfor rammen av fri egenkapital, jf. aksjeloven § 9-3. Beløpet må bekreftes mot sist godkjente årsregnskap — det kan ikke beregnes fra aksjeeierboken alene.`,
      '',
      'AKSJER OG KAPITAL',
      `Aksjer utestående før ervervet: ${nb(m.before)}.`,
      `Aksjer utestående etter ervervet: ${nb(m.after)}${
        m.capitalAfter === null ? '' : `, tilsvarende en aksjekapital på ${fmtKrSum(m.capitalAfter)}`
      }.`,
      m.pct === null
        ? ''
        : `Ervervet utgjør ${m.pct.toFixed(2).replace('.', ',')} % av aksjene.`,
      '',
      'VEDTAK',
      `Styret vedtok ervervet som beskrevet ovenfor.`,
      `Egne aksjer gir ikke stemmerett, jf. aksjeloven § 5-4 annet ledd.`,
      `Sletting av de ervervede aksjene krever eget vedtak om kapitalnedsettelse etter aksjeloven kapittel 12, med kreditorvarsel. Slik beslutning er ikke truffet i dette møtet.`,
      '',
      'ERVERVEDE AKSJER',
      `  Selger — Aksjer — Kurs — Vederlag`,
      `  ${m.seller?.name ?? '[selger]'} — ${nb(transfer.shares)} — ${
        transfer.pricePerShare === null ? '—' : fmtKrPrice(transfer.pricePerShare)
      } — ${m.consideration === null ? '—' : fmtKrExact(m.consideration)}`,
      `  SUM — ${nb(transfer.shares)} — — ${m.consideration === null ? '—' : fmtKrExact(m.consideration)}`,
      transfer.note ? '' : '',
      transfer.note ? `Grunnlag: ${transfer.note}` : '',
      !transfer.settled
        ? '\nMERK: Ervervet er ikke gjennomført. Aksjene regnes fortsatt som utestående inntil oppgjør er registrert, og protokollen er foreløpig.'
        : '',
      '',
      `Sted og dato: ______________________  (${fmtDateAbs(transfer.date)})`,
      '',
      ...(c.board.length
        ? c.board.flatMap((b) => ['', '______________________', `${b.name}${b.role ? `, ${b.role}` : ''}`])
        : ['', '______________________']),
    ]
    return lines.filter((l) => l !== '').join('\n').replace(/\n{3,}/g, '\n\n')
  }, [company, model, transfer])

  return (
    <Modal
      title="Board minutes — buy-back"
      onClose={onClose}
      width={780}
      footer={
        <>
          <span className="payreq__count">
            {nb(transfer.shares)} shares ·{' '}
            {model.consideration === null ? 'no price' : fmtKrExact(model.consideration)}
          </span>
          <button
            className="btn btn--ghost"
            onClick={() => {
              void navigator.clipboard.writeText(text)
              setCopied(true)
              window.setTimeout(() => setCopied(false), 2000)
            }}
          >
            {copied ? 'Copied' : 'Copy text'}
          </button>
          <button className="btn btn--primary" onClick={() => window.print()}>
            Print
          </button>
        </>
      }
    >
      <div className="minutes">
        {missing.length > 0 && (
          <p className="recform__warn">
            Missing from Admin → Company details: {missing.join(', ')}. The document marks those
            spots rather than inventing them.
          </p>
        )}

        {!transfer.settled && (
          <p className="recform__warn">
            This buy-back is not settled. The shares still count as outstanding everywhere in the
            app, so the minutes print as provisional and say so.
          </p>
        )}

        <p className="recform__note minutes__lede">
          A different document from a capital increase: nobody subscribes, no shares are created,
          and the money goes the other way. Free equity under § 9-3 is a gap rather than a figure —
          the register holds shares, not accounts, so it must be confirmed against the last approved
          annual accounts.
        </p>

        <div className="own__table minutes__table">
          <div className="minutes__head minutes__head--buyback">
            <div>Selger</div>
            <div>Aksjer</div>
            <div>Kurs</div>
            <div>Vederlag</div>
          </div>
          <div className="minutes__row minutes__row--buyback">
            <div className="own__trname">{model.seller?.name ?? '—'}</div>
            <div>{nb(transfer.shares)}</div>
            <div>
              {transfer.pricePerShare === null ? '—' : fmtKrPrice(transfer.pricePerShare)}
            </div>
            <div>{model.consideration === null ? '—' : fmtKrExact(model.consideration)}</div>
          </div>
          <div className="minutes__row minutes__row--buyback minutes__row--sum">
            <div>Sum</div>
            <div>{nb(transfer.shares)}</div>
            <div>—</div>
            <div>{model.consideration === null ? '—' : fmtKrExact(model.consideration)}</div>
          </div>
        </div>

        <pre className="minutes__doc">{text}</pre>

        <p className="micro">
          Retiring the shares is a separate resolution — a capital reduction under chapter 12, with
          creditor notice. This minute deliberately does not make it.
        </p>
      </div>
    </Modal>
  )
}
